/**
 * POS Receipt Detail (Admin)
 *
 * Full read-only view of a single POSTransaction by its transaction_number
 * (which equals the Helcim invoice_number). Reached by clicking an invoice
 * link on either:
 *   - the walk-in detail page (UnmatchedVisitorDetail), or
 *   - the BridgeClubMemberDetail "Recent purchases" combined table.
 *
 * The page is intentionally read-only and mirrors the layout of the legacy
 * BridgeClubPurchases summary cards — same green/cream palette, same
 * info-row cadence. Back link returns to wherever the user came from
 * (router history), falling back to the bridge-club index.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

interface ReceiptInvoiceLine {
  name: string;
  quantity_lbs: number;
  packages: number;
  line_total: number;
}

interface ReceiptInvoicePayment {
  invoice_number: string;
  applied: number;
  remaining_balance: number | null;
  paid_in_full: boolean | null;
  line_items: ReceiptInvoiceLine[];
}

interface ReceiptItem {
  product_id: number;
  product_name: string;
  sku: string | null;
  quantity: number;
  is_weight_based: boolean;
  unit_price: number;
  line_total: number;
  tax_amount: number;
  discount_amount: number;
  is_tax_exempt: boolean;
  is_red_bridge: boolean;
  // Present only on INVPAY collection lines: the invoice this payment was
  // applied to, its line items, and the remaining balance — same data the
  // printed receipt shows so the admin view matches the paper.
  invoice_payment?: ReceiptInvoicePayment | null;
}

interface ReceiptPayment {
  id: number;
  method: string;
  method_display: string;
  amount: number;
  status: string;
  card_type: string | null;
  card_last_four: string | null;
  is_credit: boolean | null;
  check_number: string | null;
  helcim_transaction_id: string | null;
  amount_tendered: number | null;
  change_given: number | null;
  healed_from_email: boolean;
  created_at: string | null;
}

interface ReceiptMember {
  id: number;
  name: string;
  email: string;
  member_code: string;
}

interface ReceiptCashier {
  id: number;
  name: string;
}

interface ReceiptDetail {
  transaction_number: string;
  pos_transaction_id: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  cashier: ReceiptCashier | null;
  register: string | null;
  bridge_club_member: ReceiptMember | null;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  surcharge_amount: number;
  total: number;
  local_points_redeemed: number;
  local_points_redeemed_amount: number;
  items: ReceiptItem[];
  payments: ReceiptPayment[];
  refund_status: string;
}

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v || 0);

const fmtDateTime = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const BridgeClubPOSReceiptDetail: React.FC = () => {
  const navigate = useNavigate();
  const { transactionNumber } = useParams<{ transactionNumber: string }>();
  const [receipt, setReceipt] = useState<ReceiptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReceipt = useCallback(async () => {
    if (!transactionNumber) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ReceiptDetail>(
        `/admin/bridge-club/pos-receipt/${transactionNumber}/`,
      );
      setReceipt(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load receipt');
    } finally {
      setLoading(false);
    }
  }, [transactionNumber]);

  useEffect(() => {
    fetchReceipt();
  }, [fetchReceipt]);

  if (loading) {
    return <div style={{ padding: 24 }}>Loading receipt…</div>;
  }
  if (error || !receipt) {
    return (
      <div style={{ padding: 24 }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'transparent', border: 'none', color: '#2D5A27', cursor: 'pointer', marginBottom: 12 }}
        >
          ← Back
        </button>
        <div style={{ color: '#B91C1C' }}>{error || 'Receipt not found'}</div>
      </div>
    );
  }

  const statusBadge = (() => {
    switch (receipt.status) {
      case 'completed':
        return { label: 'Completed', bg: '#D1FAE5', fg: '#065F46' };
      case 'voided':
        return { label: 'Voided', bg: '#FEE2E2', fg: '#991B1B' };
      case 'refunded':
        return { label: 'Refunded', bg: '#FEF3C7', fg: '#92400E' };
      default:
        return { label: receipt.status, bg: '#E5E7EB', fg: '#374151' };
    }
  })();

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: '#2D5A27',
          cursor: 'pointer',
        }}
      >
        ← Back
      </button>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 16,
        marginBottom: 4,
        flexWrap: 'wrap',
      }}>
        <h1 style={{ margin: 0, fontFamily: 'monospace', color: '#3F2021' }}>
          {receipt.transaction_number}
        </h1>
        <span style={{
          background: statusBadge.bg,
          color: statusBadge.fg,
          padding: '4px 10px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          {statusBadge.label}
        </span>
      </div>
      <div style={{ color: '#6B7280', marginBottom: 24 }}>
        POS receipt · {fmtDateTime(receipt.completed_at || receipt.started_at)}
        {receipt.register ? ` · ${receipt.register}` : ''}
        {receipt.cashier ? ` · cashier ${receipt.cashier.name}` : ''}
      </div>

      {/* Member + summary cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Attached customer</div>
          {receipt.bridge_club_member ? (
            <button
              onClick={() => navigate(`/admin/bridge-club/members/${receipt.bridge_club_member!.id}`)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#2D5A27',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 600,
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              {receipt.bridge_club_member.name}
            </button>
          ) : (
            <div style={{ fontSize: 14, color: '#9CA3AF', marginTop: 4 }}>
              Not attached
            </div>
          )}
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Subtotal</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtCurrency(receipt.subtotal)}</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Tax</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtCurrency(receipt.tax_amount)}</div>
        </div>
        <div style={{ background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#065F46' }}>Total</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#065F46' }}>{fmtCurrency(receipt.total)}</div>
        </div>
      </div>

      {/* Local points redemption if any */}
      {receipt.local_points_redeemed > 0 && (
        <div style={{
          background: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: 8,
          padding: 12,
          marginBottom: 24,
        }}>
          <strong style={{ color: '#78350F' }}>
            Bridge Club store credit applied:
          </strong>{' '}
          <span style={{ color: '#92400E' }}>
            {receipt.local_points_redeemed} points → {fmtCurrency(receipt.local_points_redeemed_amount)}
          </span>
        </div>
      )}

      {/* Line items */}
      <h2 style={{ marginBottom: 12 }}>Line items</h2>
      {receipt.items.length === 0 ? (
        <div style={{ color: '#6B7280', marginBottom: 24 }}>No line items.</div>
      ) : (
        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left', fontSize: 12, color: '#6B7280' }}>
                <th style={{ padding: 10 }}>Product</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Qty</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Unit</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Tax</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {receipt.items.map((it, idx) => (
                <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                  <td style={{ padding: 10 }}>
                    <div style={{ fontWeight: 500 }}>
                      {it.product_name}
                      {it.is_red_bridge && (
                        <span style={{
                          marginLeft: 8,
                          fontSize: 10,
                          color: '#065F46',
                          background: '#D1FAE5',
                          borderRadius: 3,
                          padding: '0 4px',
                          fontWeight: 700,
                        }}>RB</span>
                      )}
                      {it.is_tax_exempt && (
                        <span style={{
                          marginLeft: 6,
                          fontSize: 10,
                          color: '#1E40AF',
                          background: '#DBEAFE',
                          borderRadius: 3,
                          padding: '0 4px',
                          fontWeight: 700,
                        }}>TAX-EXEMPT</span>
                      )}
                    </div>
                    {it.sku && (
                      <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{it.sku}</div>
                    )}
                    {it.invoice_payment && (
                      <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid #E5E7EB' }}>
                        <div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                          Invoice {it.invoice_payment.invoice_number}
                        </div>
                        {it.invoice_payment.line_items.map((li, liIdx) => {
                          const qtyParts: string[] = [];
                          if (li.quantity_lbs > 0) qtyParts.push(`${li.quantity_lbs} lbs`);
                          if (li.packages > 0) qtyParts.push(`${li.packages} ${li.packages === 1 ? 'bag' : 'bags'}`);
                          return (
                            <div key={liIdx} style={{ fontSize: 11, color: '#6B7280', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                              <span>{li.name}{qtyParts.length ? ` · ${qtyParts.join(' / ')}` : ''}</span>
                              <span style={{ whiteSpace: 'nowrap' }}>{fmtCurrency(li.line_total)}</span>
                            </div>
                          );
                        })}
                        {it.invoice_payment.paid_in_full ? (
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#065F46', marginTop: 2 }}>PAID IN FULL</div>
                        ) : it.invoice_payment.remaining_balance != null ? (
                          <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>
                            Remaining balance: {fmtCurrency(it.invoice_payment.remaining_balance)}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 10, textAlign: 'right' }}>
                    {it.quantity}{it.is_weight_based ? ' lb' : ''}
                  </td>
                  <td style={{ padding: 10, textAlign: 'right' }}>{fmtCurrency(it.unit_price)}</td>
                  <td style={{ padding: 10, textAlign: 'right', color: '#6B7280' }}>
                    {it.tax_amount > 0 ? fmtCurrency(it.tax_amount) : '—'}
                  </td>
                  <td style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>
                    {fmtCurrency(it.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payments */}
      <h2 style={{ marginBottom: 12 }}>Payments</h2>
      {receipt.payments.length === 0 ? (
        <div style={{ color: '#6B7280' }}>No payments on record.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {receipt.payments.map((p) => (
            <div key={p.id} style={{
              background: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              padding: 12,
              display: 'grid',
              gridTemplateColumns: '120px 80px 1fr 100px',
              alignItems: 'center',
              gap: 12,
              fontSize: 13,
            }}>
              <span style={{ fontWeight: 600 }}>{p.method_display}</span>
              <span style={{
                background: p.status === 'approved' ? '#D1FAE5' : '#FEE2E2',
                color: p.status === 'approved' ? '#065F46' : '#991B1B',
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                textAlign: 'center',
              }}>
                {p.status}
              </span>
              <span style={{ color: '#6B7280' }}>
                {p.card_type && p.card_last_four && (
                  <>{p.card_type} ···· {p.card_last_four}</>
                )}
                {p.check_number && <>Check #{p.check_number}</>}
                {p.amount_tendered != null && p.change_given != null && (
                  <>Tendered {fmtCurrency(p.amount_tendered)} · Change {fmtCurrency(p.change_given)}</>
                )}
                {p.helcim_transaction_id && (
                  <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>
                    Helcim {p.helcim_transaction_id}
                  </span>
                )}
                {p.healed_from_email && (
                  <span style={{
                    marginLeft: 8,
                    fontSize: 10,
                    color: '#92400E',
                    background: '#FEF3C7',
                    borderRadius: 3,
                    padding: '0 4px',
                    fontWeight: 700,
                  }}>HEALED</span>
                )}
              </span>
              <span style={{ textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BridgeClubPOSReceiptDetail;
